/**
 * t331u B: phone person settings (Users › person tabs on desktop), shared by phone web and native so the
 * fields, options and what Save sends match desktop exactly.
 */
import { DateTime } from 'luxon';
import type { AvailabilityRule, Employee, EmployeeSkill, EmploymentType, Skill } from '../workforce/types';
import type { WorkforceRole } from '../types';
import { DEFAULT_HOLIDAY_RATE, fixedAllowanceHours } from '../workforce/leave';
import { CONTRACT_TYPE_OPTIONS } from '../workforce/contract';

/** Person tabs with a phone page; the rest of /settings/people/users/:id stays "best on a computer". */
export const PHONE_PERSON_TABS = ['employment', 'positions', 'skills', 'availability', 'hr'] as const;
export type PhonePersonTab = (typeof PHONE_PERSON_TABS)[number];
export const PHONE_PERSON_TAB_TITLE: Record<PhonePersonTab, string> = { employment: 'Employment and buildings', positions: 'Positions', skills: 'Skills & licences', availability: 'Availability', hr: 'Personal details' };

/** `/settings/people/users/<id>?tab=<phone tab>` → the phone page to show, or null (desktop-only card). */
export function phonePersonSettings(path: string, search = ''): { userId: string; tab: PhonePersonTab } | null {
  const m = /^\/settings\/people\/users\/([0-9a-f-]{36})\/?$/i.exec(path);
  if (!m) return null;
  const tab = new URLSearchParams(search).get('tab') ?? 'employment';
  return (PHONE_PERSON_TABS as readonly string[]).includes(tab) ? { userId: m[1]!, tab: tab as PhonePersonTab } : null;
}

export const EMPLOYMENT_TYPES: { id: EmploymentType; label: string }[] = CONTRACT_TYPE_OPTIONS;
export const WORKFORCE_ROLES: { id: WorkforceRole; label: string }[] = [
  { id: 'employee', label: 'Employee' }, { id: 'supervisor', label: 'Supervisor' }, { id: 'manager', label: 'Manager' }, { id: 'admin', label: 'Admin' },
];
/** t484u: one plain line each, shown muted under the field (desktop, phone web and iOS). */
export const ROTA_ACCESS_HINT = 'Sets what they can do on the rota at the buildings they work at.';
export const EMPLOYMENT_HINTS = {
  rotaAccess: ROTA_ACCESS_HINT,
  worksAt: 'The starred building is their primary location.',
  startDate: 'Used to pro-rate their first holiday year.',
  dateOfBirth: 'Used for young-worker rules.',
  holidayRate: '12.07% is the statutory rate.',
} as const;
/** t484u: the Employment tab's groups, in order (Holiday entitlement is its own box after these). */
export const EMPLOYMENT_GROUPS = ['Role', 'Contract', 'Dates', 'Payroll'] as const;
/** The person's own language choice, as one muted read-only line ("Language: English · set by them"). */
export const languageLine = (name: string | null) => `Language: ${name ?? 'Not set'} · set by them in their own settings`;

type Terms = Pick<Employee, 'contracted_hours_per_week' | 'contracted_days_per_week' | 'contracted_min_days_per_week' | 'contracted_max_days_per_week'>;
export type DayPattern = 'any' | 'fixed' | 'range';
export const DAY_PATTERNS: { id: DayPattern; label: string }[] = [
  { id: 'any', label: 'Any days' }, { id: 'fixed', label: 'Fixed number of days' }, { id: 'range', label: 'Between min and max days' },
];
export const dayPatternOf = (e: Terms): DayPattern =>
  e.contracted_days_per_week ? 'fixed' : (e.contracted_min_days_per_week != null || e.contracted_max_days_per_week != null) ? 'range' : 'any';
/** Switching pattern clears the fields the other patterns use, so the stored contract is never ambiguous. */
export function withPattern<T extends Terms>(e: T, p: DayPattern): T {
  if (p === 'any') return { ...e, contracted_days_per_week: null, contracted_min_days_per_week: null, contracted_max_days_per_week: null };
  if (p === 'fixed') return { ...e, contracted_days_per_week: e.contracted_days_per_week || 5, contracted_min_days_per_week: null, contracted_max_days_per_week: null };
  const hasRange = e.contracted_min_days_per_week != null || e.contracted_max_days_per_week != null;
  return { ...e, contracted_days_per_week: null, contracted_max_days_per_week: hasRange ? e.contracted_max_days_per_week : 5 };
}

export const HOLIDAY_KEYS = ['holiday_method', 'holiday_rate', 'holiday_allowance', 'holiday_allowance_unit', 'holiday_year_start_month', 'holiday_year_start_day',
  'holiday_bank_holidays', 'holiday_carry_over_max_hours'] as const;
export const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const BANK_HOLIDAY_OPTIONS: { id: '' | 'included' | 'on_top'; label: string }[] = [
  { id: '', label: 'Workspace default' }, { id: 'included', label: 'Included in the allowance' }, { id: 'on_top', label: 'On top of the allowance' },
];
const pct = (r: number | null | undefined) => `${Math.round((r ?? DEFAULT_HOLIDAY_RATE) * 10000) / 100}%`;
const hrs = (h: number) => `${Math.round(h * 100) / 100}h`;
/** The one-line holiday summary under the fields (same words as desktop). */
export function holidaySummary(e: Employee): string {
  const m = e.holiday_method ?? 'fixed';
  if (m === 'rolled_up') return `Holiday pay is included in wages at ${pct(e.holiday_rate)}. No paid allowance to book; unpaid time off and availability still work.`;
  if (m === 'accrued') return `Earns ${pct(e.holiday_rate)} of approved hours worked as bookable leave.`;
  const h = fixedAllowanceHours(e);
  return e.holiday_allowance != null ? `${e.holiday_allowance} ${e.holiday_allowance_unit ?? 'days'} a year${(e.holiday_allowance_unit ?? 'days') === 'days' && h ? ` (${hrs(h)})` : ''}, pro-rated for starters and leavers.`
    : `5.6 weeks pro-rata to weekly hours${h ? ` (${hrs(h)} a year)` : ''}, pro-rated for starters and leavers.`;
}

/** Order-insensitive for the places, so re-adding the same buildings isn't a change. */
const norm = (x: Employee) => JSON.stringify({ ...x, location_ids: [...x.location_ids].sort() });
export const employmentDirty = (e: Employee, emp: Employee) => norm(e) !== norm(emp);

/**
 * t560u: the fields Employment's Save may send (the API's EmployeeDto). An allowlist, not "everything the GET returned
 * minus a few": the GET also carries read-only fields (pay type and salary since t527u, the 48-hour opt-out, identity),
 * and sending one back made the API refuse every save with a whitelist error.
 */
export const EMPLOYMENT_SAVE_KEYS = ['job_title', 'workforce_role', 'employment_type', 'contracted_hours_per_week', 'contracted_days_per_week',
  'contracted_min_days_per_week', 'contracted_max_days_per_week', 'hourly_rate_pence', 'date_of_birth', 'employment_start_date', 'employment_end_date',
  'payroll_id', 'pay_frequency', 'primary_location_id', 'max_hours_per_week', 'entitlement_override_hours', ...HOLIDAY_KEYS, 'location_ids'] as const satisfies readonly (keyof Employee)[];

/**
 * What Employment's Save sends (desktop EmploymentForm and phone): only EMPLOYMENT_SAVE_KEYS, so never positions (their
 * own tab), the 48-hour opt-out (only changes by signing) or pay type / salary (Bulk edit); holiday settings only when
 * changed (saving them counts as the review of a migrated contract); the job title only when changed.
 */
export function employmentPatch(e: Employee, emp: Employee): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of EMPLOYMENT_SAVE_KEYS) if (e[k] !== undefined) out[k] = e[k];
  for (const k of HOLIDAY_KEYS) if (JSON.stringify(e[k] ?? null) === JSON.stringify(emp[k] ?? null)) delete out[k];
  if ((e.job_title ?? null) === (emp.job_title ?? null)) delete out.job_title;
  // t605u: pay frequency only when changed (an API older than the field, or a viewer it is hidden from, never gets it back).
  if ((e.pay_frequency ?? null) === (emp.pay_frequency ?? null)) delete out.pay_frequency;
  // t714u: date of birth is edited on Personal details; Employment never sends it back unless it changed.
  if ((e.date_of_birth ?? null) === (emp.date_of_birth ?? null)) delete out.date_of_birth;
  return out;
}

/** Number field text → number or null (blank). */
export const numOrNull = (v: string) => (v.trim() === '' || Number.isNaN(Number(v)) ? null : Number(v));
/** "12.50" → 1250 pence, blank → null. */
export const poundsToPence = (v: string) => (v.trim() === '' || Number.isNaN(Number(v)) ? null : Math.round(Number(v) * 100));
/** "2026-03-01" → "01/03/2026" (blank for null). */
export const isoToGb = (iso: string | null | undefined) => (iso && /^\d{4}-\d{2}-\d{2}$/.test(iso) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '');
/** Typed en-GB date ("1/3/2026", "01/03/2026") → ISO; '' → null; anything else (half typed, 31/02) → undefined. */
export function gbToIso(v: string): string | null | undefined {
  const t = v.trim();
  if (!t) return null;
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  if (!m) return undefined;
  const d = Number(m[1]), mo = Number(m[2]), y = Number(m[3]);
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return undefined;
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

// Positions (desktop PositionsTab / PositionPicker).
export interface PositionValue { ids: string[]; primary: string | null }
/** Keep the primary valid: removing it falls back to the first remaining; a first position becomes primary. */
export function normalisePositions(v: PositionValue): PositionValue {
  const ids = [...new Set(v.ids)];
  const primary = v.primary && ids.includes(v.primary) ? v.primary : ids[0] ?? null;
  return { ids, primary };
}
export const samePositions = (a: PositionValue, b: PositionValue) => a.primary === b.primary && [...a.ids].sort().join() === [...b.ids].sort().join();
/** What Positions' Save sends (the same as desktop). */
export const positionsPatch = (v: PositionValue) => ({ position_ids: v.ids, primary_position_id: v.primary });
export const POSITIONS_HISTORY_ACTIONS = ['workforce.employee.positions_changed'];

type Named = { id: string; name: string | null } | null;
/** One line per positions change: "Added Picker, Driver · removed Forklift · primary Driver (was Picker)". */
export function describePositionsChange(m: Record<string, unknown>): string {
  const list = (x: unknown) => ((x as Named[] | undefined) ?? []).map(p => p?.name ?? 'a deleted position');
  const parts: string[] = [];
  const add = list(m.added); const rem = list(m.removed);
  if (add.length) parts.push(`Added ${add.join(', ')}`);
  if (rem.length) parts.push(`${parts.length ? 'removed' : 'Removed'} ${rem.join(', ')}`);
  const p = m.primary as { from: Named; to: Named } | undefined;
  if (p) parts.push(`${parts.length ? 'primary' : 'Primary'} ${p.to?.name ?? 'none'}${p.from ? ` (was ${p.from.name ?? 'a deleted position'})` : ''}`);
  return parts.join(' · ') || 'No change';
}

// Skills & licences (desktop SkillsPage EmployeeSkills / RecordModal).
export const SKILL_STATUS_LABEL: Record<NonNullable<EmployeeSkill['status']>, string> = { valid: 'Valid', no_expiry: 'No expiry', expiring: 'Expiring soon', expired: 'Expired', missing: 'Missing' };
/** "Expiring soon · Expires 1 Nov 2026", or just "No expiry" (no repeat when that is the status). */
export function skillStatusLine(r: Pick<EmployeeSkill, 'status' | 'expires_on'>, day: (iso: string) => string): string {
  const when = r.expires_on ? `Expires ${day(r.expires_on)}` : 'No expiry';
  const status = r.status ? SKILL_STATUS_LABEL[r.status] : null;
  return status && status !== when ? `${status} · ${when}` : when;
}
export interface SkillDraft { skill_id: string; issued_on: string; expires_on: string; reference: string; notes: string; evidence_attachment_id: string | null; evidence_name: string }
export const skillDraft = (r: Partial<EmployeeSkill>, skills: Pick<Skill, 'id'>[]): SkillDraft => ({
  skill_id: r.skill_id ?? skills[0]?.id ?? '', issued_on: r.issued_on ?? '', expires_on: r.expires_on ?? '', reference: r.reference ?? '', notes: r.notes ?? '',
  evidence_attachment_id: r.evidence_attachment_id ?? null, evidence_name: r.evidence_file_name ?? '',
});
/** The expiry the server fills in when Expires is left blank: issued + the skill's validity, less a day. */
export function suggestedExpiry(skill: Pick<Skill, 'default_validity_months'> | undefined, f: Pick<SkillDraft, 'issued_on' | 'expires_on'>): string | null {
  if (!skill?.default_validity_months || !f.issued_on || f.expires_on) return null;
  return DateTime.fromISO(f.issued_on).plus({ months: skill.default_validity_months }).minus({ days: 1 }).toISODate();
}
/** Save is blocked when the skill needs an expiry and none is given or suggested. */
export const skillNeedsExpiry = (skill: Pick<Skill, 'requires_expiry' | 'default_validity_months'> | undefined, f: SkillDraft) => Boolean(skill?.requires_expiry && !f.expires_on && !suggestedExpiry(skill, f));
/** What Save sends (the same as desktop). */
export const skillPayload = (userId: string, f: SkillDraft) => ({
  user_id: userId, skill_id: f.skill_id, issued_on: f.issued_on || null, expires_on: f.expires_on || null,
  reference: f.reference.trim() || null, notes: f.notes.trim() || null, evidence_attachment_id: f.evidence_attachment_id,
});
export const SKILL_EVIDENCE_NOTE = 'The employee and their managers are reminded before it expires. Evidence is visible to the employee and people who manage them.';

// Availability (desktop MyShiftsPage AvailabilityEditor).
export type AvailabilityKind = AvailabilityRule['kind'];
export const AVAILABILITY_KINDS: { id: AvailabilityKind; label: string }[] = [{ id: 'unavailable', label: 'Unavailable' }, { id: 'available', label: 'Available' }, { id: 'preferred', label: 'Preferred' }];
export const WEEKDAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export const AVAILABILITY_NOTE = "Managers see a warning if they schedule someone when they're unavailable. Booked holiday is handled under Time off.";
export interface AvailabilityDraft { weekday: number; date: string; kind: AvailabilityKind; start_time: string; end_time: string; note: string }
export const emptyAvailability = (): AvailabilityDraft => ({ weekday: 1, date: '', kind: 'unavailable', start_time: '', end_time: '', note: '' });
/** "09:00–13:00", or "All day" when either end is open. */
export const availabilityTimes = (r: Pick<AvailabilityRule, 'start_time' | 'end_time'>) => (r.start_time && r.end_time ? `${r.start_time.slice(0, 5)}–${r.end_time.slice(0, 5)}` : 'All day');
/** Both times or neither; From before To. */
export function availabilityTimesError(d: Pick<AvailabilityDraft, 'start_time' | 'end_time'>): string | null {
  if (Boolean(d.start_time) !== Boolean(d.end_time)) return 'Add both times, or leave both blank for all day.';
  if (d.start_time && d.end_time <= d.start_time) return 'To must be after From.';
  return null;
}
/** The weekly pattern by day, then time. */
export const sortRules = <T extends Pick<AvailabilityRule, 'weekday' | 'start_time'>>(rules: T[]) => [...rules].sort((a, b) => a.weekday - b.weekday || (a.start_time ?? '').localeCompare(b.start_time ?? ''));
/** What Add sends (the same as desktop). */
export const availabilityRuleBody = (userId: string | undefined, d: AvailabilityDraft) => ({
  user_id: userId, weekday: d.weekday, kind: d.kind, start_time: d.start_time || null, end_time: d.end_time || null, note: d.note || null, effective_from: null, effective_to: null,
});
export const availabilityExceptionBody = (userId: string | undefined, d: AvailabilityDraft) => ({
  user_id: userId, date: d.date, kind: d.kind, start_time: d.start_time || null, end_time: d.end_time || null, note: d.note || null,
});
